import * as React from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { EmptyState } from '@redhand/ui';
import { systemClock } from '@reos/config';
import { Plus } from 'lucide-react';
import { PageHead } from '../components/page-head.js';
import { DemandColumn } from '../components/demand-column.js';
import { DemandDrawer } from '../components/demand-drawer.js';
import { ListTable, type ListColumn } from '../components/list-table.js';
import {
  createDemand,
  importPortalLeads,
  listDemandStages,
  listDemands,
  transitionDemand,
  updateDemand,
  type Demand,
  type DemandStage,
} from '../lib/demands-api.js';
import { createClientFromDemand } from '../lib/demand-to-client.js';
import { DomainApiError } from '../lib/api-error.js';
import { focusField, issueForField, normalizeNumberInput } from '../lib/form-errors.js';
import { daysInStage, demandSummary, groupByStage } from '../lib/demand-stages.js';
import { listMembers, type TenantMember } from '../lib/members-api.js';
import { useSession, readDemandViewPreference, writeDemandViewPreference } from '../stores/session.js';
import { useT } from '../lib/i18n.js';

/** Krok SR2: kontrakt z OpenAPI je bez `readAt` (generován před SR2) - stejný
 * důvod jako `UpdateDemandBody` v `demands-api.ts`. */
type DemandWithRead = Demand & { readonly readAt: string | null };

/** Kolik poptávek nacte jedna stránka — hlaska o oříznutí používá tuto hodnotu. */
const DEMAND_PAGE_LIMIT = 100;

const SOURCE_OPTIONS = ['portal', 'web', 'phone', 'manual'] as const;

/**
 * Krok UXL1 bod 3 — pipeline je VÝCHOZÍ jako seznam, tabule (kanban) je
 * volba. Predvolba prohlizece (klic `reos.poptavky.view`), stejny vzor jako
 * `readCaseViewPreference` u `/zakazky`.
 */
const PIPELINE_VIEWS = ['list', 'kanban'] as const;
type PipelineView = (typeof PIPELINE_VIEWS)[number];

function readStoredPipelineView(): PipelineView {
  const raw = readDemandViewPreference();
  return raw !== null && (PIPELINE_VIEWS as readonly string[]).includes(raw) ? (raw as PipelineView) : 'list';
}

/**
 * `/poptavky` — pipeline poptávek a zájemců (krok CRM1).
 *
 * Kanban jako u zakázek: sloupce = konfigurace kanceláře z API, přesun =
 * `POST /v1/crm/demands/:id/transition`, povolené cíle chodí z API
 * (`allowedTransitions`), takže UI nedoplňuje stavový automat. Karta otevírá
 * drawer s historií aktivit (vzorem `DealDrawer` z crm-web).
 */
export function PoptavkyPage(): React.JSX.Element {
  const t = useT();
  const currentUserId = useSession((s) => s.user?.id ?? null);
  const [demands, setDemands] = React.useState<Demand[] | null>(null);
  const [stages, setStages] = React.useState<readonly DemandStage[]>([]);
  const [members, setMembers] = React.useState<readonly TenantMember[]>([]);
  const [pipelineView, setPipelineView] = React.useState<PipelineView>(() => readStoredPipelineView());
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [truncated, setTruncated] = React.useState(false);
  const [draggingAllowed, setDraggingAllowed] = React.useState<readonly string[] | null>(null);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [source, setSource] = React.useState<string>('');
  const [mineOnly, setMineOnly] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [showImport, setShowImport] = React.useState(false);
  const [now] = React.useState(() => systemClock.now());
  // Krok SR2: "Ohlasy z portálů" - jeden seznam reakcí ze všech portálů vedle kanbanu.
  const [view, setView] = React.useState<'kanban' | 'inbox'>('kanban');

  // 8px aktivace: bez ní PointerSensor pohltí běžný klik na kartu a „klik ->
  // detail" by se nikdy nedosáhl (stejný důvod jako v `zakazky.tsx`).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Sloupce a poptavky jdou dvěma requesty, ne jedním: sloupce jsou
      // konfigurace kanceláře (malé, jinde v UI) a poptávky fronta.
      const [stageResult, demandResult, memberResult] = await Promise.all([
        listDemandStages(),
        listDemands({ limit: DEMAND_PAGE_LIMIT }),
        listMembers().catch(() => [] as TenantMember[]),
      ]);
      setStages(stageResult);
      setDemands(demandResult.items);
      setMembers(memberResult);
      setTruncated(demandResult.nextCursor !== null);
    } catch (e) {
      setLoadError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const filtered = React.useMemo(() => {
    if (demands === null) return [];
    const q = search.trim().toLowerCase();
    return demands.filter((d) => {
      if (mineOnly && d.ownerUserId !== currentUserId) return false;
      if (source !== '' && d.source !== source) return false;
      if (q === '') return true;
      const haystack = [d.title, d.contactName ?? '', d.contactEmail ?? '', d.wantedLocality ?? '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [demands, mineOnly, source, search, currentUserId]);

  // Krok SR2 bod 4: upozorneni makleri na novou reakci v aplikaci. Pocita se nad
  // VSEMI nactenymi poptavkami, ne nad `filtered` - odznak sedi nad filtry, takze
  // by lhal, kdyby ho hledani nebo "jen moje" umelo schovat.
  const unreadPortalCount = React.useMemo(
    () =>
      (demands ?? []).filter((d) => d.source === 'portal' && (d as DemandWithRead).readAt === null).length,
    [demands],
  );

  const groups = React.useMemo(() => groupByStage(filtered, stages), [filtered, stages]);
  const knownStageKeys = React.useMemo(() => new Set(stages.map((s) => s.key)), [stages]);
  const memberNameById = React.useMemo(() => new Map(members.map((m) => [m.userId, m.name])), [members]);

  function changePipelineView(next: PipelineView): void {
    setPipelineView(next);
    writeDemandViewPreference(next);
  }

  function handleDragStart(event: DragStartEvent): void {
    setActionError(null);
    const data = event.active.data.current as { allowedTransitions?: readonly string[] } | undefined;
    setDraggingAllowed(data?.allowedTransitions ?? []);
  }

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    setDraggingAllowed(null);
    const { active, over } = event;
    if (over === null) return;
    const target = String(over.id);
    const data = active.data.current as { stage?: string; allowedTransitions?: readonly string[] } | undefined;
    if (data?.stage === target) return;
    if (!data?.allowedTransitions?.includes(target)) return;
    const id = String(active.id);
    try {
      const updated = await transitionDemand(id, target);
      setDemands((prev) => (prev === null ? prev : prev.map((d) => (d.id === updated.id ? updated : d))));
    } catch (e) {
      setActionError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.moveFailed'));
    }
  }

  function openDemand(id: string): void {
    setOpenId(id);
  }

  function onDrawerChanged(updated: Demand): void {
    setDemands((prev) => (prev === null ? prev : prev.map((d) => (d.id === updated.id ? updated : d))));
  }

  return (
    <div data-testid="poptavky-page">
      <PageHead title={t('webAdmin.demands.pageTitle')} />
      <div className="p-6">
        <div className="mb-4 flex items-center justify-end gap-3">
          <button
            type="button"
            className="rh-btn"
            data-testid="demand-import-open"
            onClick={() => setShowImport((v) => !v)}
          >
            {t('webAdmin.demands.import')}
          </button>
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            data-testid="demand-new"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus size={16} aria-hidden="true" /> {t('webAdmin.demands.newDemand')}
          </button>
        </div>

        <div className="mb-4 flex overflow-hidden rounded border" data-testid="demand-view-tabs">
          <button
            type="button"
            className={`px-3 py-1 text-sm ${view === 'kanban' ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
            data-testid="demand-view-kanban"
            onClick={() => setView('kanban')}
          >
            {t('webAdmin.demands.inbox.tabKanban')}
          </button>
          <button
            type="button"
            className={`px-3 py-1 text-sm ${view === 'inbox' ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
            data-testid="demand-view-inbox"
            onClick={() => setView('inbox')}
          >
            {t('webAdmin.demands.inbox.tabInbox')}
            {unreadPortalCount > 0 && (
              <span
                className="rh-a2-badge rh-a2-badge--accent ml-2"
                data-testid="demand-view-inbox-badge"
                aria-label={t('webAdmin.demands.inbox.unreadCount', { count: String(unreadPortalCount) })}
              >
                {unreadPortalCount}
              </span>
            )}
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex overflow-hidden rounded border">
            <button
              type="button"
              className={`px-3 py-1 text-sm ${!mineOnly ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
              data-testid="demand-filter-office"
              onClick={() => setMineOnly(false)}
            >
              {t('webAdmin.kanban.wholeOffice')}
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-sm ${mineOnly ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
              data-testid="demand-filter-mine"
              onClick={() => setMineOnly(true)}
            >
              {t('webAdmin.kanban.mine')}
            </button>
          </div>

          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="border px-2 py-1 text-sm"
            data-testid="demand-filter-source"
            aria-label={t('webAdmin.demands.sourceLabel')}
          >
            <option value="">{t('webAdmin.demands.allSources')}</option>
            {SOURCE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`crm.source.${option}` as 'crm.source.portal')}
              </option>
            ))}
          </select>

          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('webAdmin.demands.searchLabel')}
            className="border px-2 py-1 text-sm"
            data-testid="demand-search"
            aria-label={t('webAdmin.demands.searchLabel')}
          />
        </div>

        {showImport && (
          <ImportPanel
            onDone={(created, merged) => {
              setNotice(
                t('webAdmin.demands.importDone', { created: String(created), merged: String(merged) }),
              );
              setShowImport(false);
              void load();
            }}
            onError={(message) => setActionError(message)}
          />
        )}

        {showForm && (
          <DemandForm
            onCreated={() => {
              setNotice(t('webAdmin.demands.form.created'));
              setShowForm(false);
              void load();
            }}
            onError={(message) => setActionError(message)}
          />
        )}

        {notice !== null && (
          <p role="status" className="mb-3" data-testid="demand-notice">
            {notice}
          </p>
        )}
        {loading && <p role="status">{t('webAdmin.demands.loading')}</p>}
        {loadError !== null && (
          <p role="alert" className="mb-4 text-red-600" data-testid="demand-load-error">
            {loadError}
          </p>
        )}
        {actionError !== null && (
          <p role="alert" className="mb-4 text-red-600" data-testid="demand-action-error">
            {actionError}
          </p>
        )}
        {truncated && (
          <p className="mb-2 text-xs text-gray-500" data-testid="demand-truncated">
            {t('webAdmin.demands.truncated', { limit: String(DEMAND_PAGE_LIMIT) })}
          </p>
        )}

        {/* Krok UXL1 bod 3: seznam je výchozí, tabule je volba — přepínač
            patří jen k pipeline, ne k „Ohlasům z portálů". */}
        {view === 'kanban' && (
          <div className="mb-4 flex overflow-hidden rounded border" data-testid="demand-pipeline-view-tabs">
            <button
              type="button"
              className={`px-3 py-1 text-sm ${pipelineView === 'list' ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
              data-testid="demand-pipeline-view-list"
              onClick={() => changePipelineView('list')}
            >
              {t('webAdmin.demands.viewToggleList')}
            </button>
            <button
              type="button"
              className={`px-3 py-1 text-sm ${pipelineView === 'kanban' ? 'bg-gray-900 text-rh-on-accent' : 'bg-rh-card'}`}
              data-testid="demand-pipeline-view-board"
              onClick={() => changePipelineView('kanban')}
            >
              {t('webAdmin.demands.viewToggleBoard')}
            </button>
          </div>
        )}

        {!loading && loadError === null && filtered.length === 0 ? (
          <div data-testid="poptavky-empty">
            <EmptyState
              title={t('webAdmin.demands.emptyTitle')}
              description={t('webAdmin.demands.emptyDescription')}
            />
          </div>
        ) : view === 'kanban' && pipelineView === 'list' ? (
          <DemandListView items={filtered} now={now} memberNameById={memberNameById} onOpen={openDemand} />
        ) : view === 'kanban' ? (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(e) => void handleDragEnd(e)}>
            <div className="rh-kanban-board">
              {groups.map(({ stage, items }) => (
                <DemandColumn
                  key={stage.key}
                  stage={stage}
                  items={items}
                  now={now}
                  onOpen={openDemand}
                  dropAllowed={draggingAllowed === null ? undefined : draggingAllowed.includes(stage.key)}
                  unknown={!knownStageKeys.has(stage.key)}
                />
              ))}
            </div>
          </DndContext>
        ) : (
          <PortalInboxList
            items={filtered.filter((d) => d.source === 'portal')}
            onOpen={openDemand}
            onChanged={onDrawerChanged}
            onError={(message) => setActionError(message)}
          />
        )}

        {openId !== null && (
          <DemandDrawer
            demandId={openId}
            stages={stages}
            onClose={() => setOpenId(null)}
            onChanged={onDrawerChanged}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Krok UXL1 bod 3 — VÝCHOZÍ pohled pipeline. Řádek nese to, co makléř na
 * první pohled potřebuje: KONTAKT (ne titulek nemovitosti — ten je o
 * nemovitosti, ne o člověku), co zájemce hledá, stáří poptávky, makléř a
 * další krok. Klik na řádek otevírá TENTÝŽ `DemandDrawer` jako kanban karta
 * (`demand-card-<id>` — sdílený testid, žádná druhá cesta k detailu).
 */
function DemandListView(props: {
  readonly items: readonly Demand[];
  readonly now: Date;
  readonly memberNameById: ReadonlyMap<string, string>;
  readonly onOpen: (id: string) => void;
}): React.JSX.Element {
  const t = useT();

  /**
   * Krok UXF2 bod 6 (nalez N5) — SEZNAM JDE PRES SDILENOU `ListTable`.
   *
   * Vlastni `<table class="rh-table">` nechaval na 390 px viditelne zahlavi
   * tabulky (media dotaz v `admin-v2.css` míri na `.rh-table table thead`,
   * tedy na tabulku KITU uvnitr obalky — ne na tabulku s tou tridou) a karty
   * pod nim byly bez popisku hodnot. `ListTable` bere popisek hlavicky
   * i popisek karty z TEHOZ retezce, takze se nemohou rozejit.
   */
  return (
    <ListTable
      testId="demand-list"
      caption={t('webAdmin.demands.listColContact')}
      rows={props.items}
      rowKey={(item) => item.id}
      rowTestId={(item) => `demand-card-${item.id}`}
      onRowClick={(item) => props.onOpen(item.id)}
      columns={[
        {
          key: 'contact',
          label: t('webAdmin.demands.listColContact'),
          cell: (item) => (
            <>
              <div>{item.contactName ?? item.title}</div>
              {item.contactPhone !== null && item.contactPhone !== '' ? (
                <div className="text-xs text-gray-500">{item.contactPhone}</div>
              ) : null}
            </>
          ),
        },
        {
          key: 'wants',
          label: t('webAdmin.demands.listColWants'),
          cell: (item) => <span data-testid={`demand-summary-${item.id}`}>{demandSummary(item) ?? '—'}</span>,
        },
        {
          key: 'age',
          label: t('webAdmin.demands.listColAge'),
          cell: (item) => {
            const days = daysInStage(item.stageEnteredAt, props.now);
            return days === 0
              ? t('webAdmin.demands.listAgeToday')
              : t('webAdmin.demands.listAgeDays', { days: String(days) });
          },
        },
        {
          key: 'broker',
          label: t('webAdmin.demands.listColBroker'),
          cell: (item) =>
            (item.ownerUserId === null ? null : (props.memberNameById.get(item.ownerUserId) ?? null)) ??
            t('webAdmin.demands.listNoBroker'),
        },
        {
          key: 'nextStep',
          label: t('webAdmin.demands.listColNextStep'),
          cell: (item) =>
            item.nextStep !== null && item.nextStep !== ''
              ? item.nextStep
              : t('webAdmin.demands.listNoNextStep'),
        },
      ]}
    />
  );
}

/**
 * "Ohlasy z portálů" (krok SR2, bod 3 zadání) — JEDEN seznam reakcí ze
 * všech portálů (Sreality/RealityMix/SuperHome/e-mail notifikace), bez
 * ohledu na to, kterým kanálem přišly; `source_portal` ukazuje odkud, UI
 * zdroj systému neukazuje (CLAUDE.md zadání R.B.: "v UI nejmenovat zdroje
 * dat" se týká NÁS, ne portálu — ten je informace pro makléře).
 *
 * "Odeslat nabídku e-mailem" otevře stejný `DemandDrawer` jako kanban (kde
 * žije `MatchesPanel` s `previewOffer`/`sendOffer` z CRM4) — druhé odesílání
 * nabídky by bylo druhá implementace téhož konceptu (CLAUDE.md §0).
 */
function PortalInboxList(props: {
  readonly items: readonly Demand[];
  readonly onOpen: (id: string) => void;
  readonly onChanged: (demand: Demand) => void;
  readonly onError: (message: string) => void;
}): React.JSX.Element {
  const t = useT();
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const sorted = React.useMemo(
    () => [...props.items].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [props.items],
  );

  async function toggleRead(demand: Demand): Promise<void> {
    const isUnread = (demand as DemandWithRead).readAt === null;
    setBusyId(demand.id);
    try {
      const updated = await updateDemand(demand.id, { readAt: isUnread ? systemClock.now().toISOString() : null });
      props.onChanged(updated);
    } catch (e) {
      props.onError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.inbox.actionFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function createClient(demand: Demand): Promise<void> {
    setBusyId(demand.id);
    try {
      const updated = await createClientFromDemand(demand);
      props.onChanged(updated);
    } catch (e) {
      props.onError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.inbox.createClientFailed'));
    } finally {
      setBusyId(null);
    }
  }

  if (sorted.length === 0) {
    return (
      <div data-testid="demand-inbox-empty">
        <EmptyState
          title={t('webAdmin.demands.inbox.emptyTitle')}
          description={t('webAdmin.demands.inbox.emptyDescription')}
        />
      </div>
    );
  }

  /*
    Krok UXD1b (ZBYVA z UXF2) — OHLASY Z PORTALU NA SPOLECNE `ListTable`.
    Zalozka mela vlastni `<table class="rh-table">`, tedy stejnou vadu, jakou
    UXF2 bod 6 opravil u seznamu poptavek: media dotaz `admin-v2.css` dela
    z radku kartu jen tabulkam `.rh-a2-tbl`, takze na 390 px tu zustalo
    zahlavi a hodnoty bez popisku. Popisek karty a hlavicka sloupce jdou nove
    z JEDNOHO retezce (`ListColumn.label`).
  */
  const columns: readonly ListColumn<Demand>[] = [
    {
      key: 'date',
      label: t('webAdmin.demands.inbox.colDate'),
      cell: (demand) =>
        new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(demand.createdAt),
        ),
    },
    {
      key: 'client',
      label: t('webAdmin.demands.inbox.colClient'),
      cell: (demand) => (
        <button type="button" className="rh-link" onClick={() => props.onOpen(demand.id)}>
          {demand.contactName ?? t('webAdmin.demands.noContact')}
        </button>
      ),
    },
    {
      key: 'email',
      label: t('webAdmin.demands.inbox.colEmail'),
      cell: (demand) => demand.contactEmail ?? '—',
    },
    {
      key: 'phone',
      label: t('webAdmin.demands.inbox.colPhone'),
      cell: (demand) => demand.contactPhone ?? '—',
    },
    {
      key: 'portal',
      label: t('webAdmin.demands.inbox.colPortal'),
      cell: (demand) => demand.sourcePortal ?? '—',
    },
    {
      key: 'message',
      label: t('webAdmin.demands.inbox.colMessage'),
      cell: (demand) => demand.wantedNotes ?? '—',
    },
    {
      key: 'actions',
      label: t('webAdmin.demands.inbox.colActions'),
      srOnlyLabel: true,
      align: 'right',
      cell: (demand) => (
        <span className="rh-table__actions">
          <button
            type="button"
            className="rh-btn"
            disabled={busyId === demand.id}
            data-testid={`demand-inbox-offer-${demand.id}`}
            onClick={() => props.onOpen(demand.id)}
          >
            {t('webAdmin.demands.inbox.sendOffer')}
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={busyId === demand.id || demand.partyId !== null || (demand.contactEmail === null && demand.contactPhone === null)}
            data-testid={`demand-inbox-create-client-${demand.id}`}
            onClick={() => void createClient(demand)}
          >
            {demand.partyId !== null ? t('webAdmin.demands.inbox.clientCreated') : t('webAdmin.demands.inbox.createClient')}
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={busyId === demand.id}
            data-testid={`demand-inbox-toggle-read-${demand.id}`}
            onClick={() => void toggleRead(demand)}
          >
            {(demand as DemandWithRead).readAt === null
              ? t('webAdmin.demands.inbox.markRead')
              : t('webAdmin.demands.inbox.markUnread')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <ListTable
      testId="demand-inbox-table"
      caption={t('webAdmin.demands.inbox.tabInbox')}
      columns={columns}
      rows={sorted}
      rowKey={(demand) => demand.id}
      rowTestId={(demand) => `demand-inbox-row-${demand.id}`}
    />
  );
}

/** Ruční import jednoho ohlasu — stejná cesta, jakou později půjde dávkový import z konektoru. */
function ImportPanel(props: {
  readonly onDone: (created: number, merged: number) => void;
  readonly onError: (message: string) => void;
}): React.JSX.Element {
  const t = useT();
  const [portal, setPortal] = React.useState('sreality');
  const [listingRef, setListingRef] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const result = await importPortalLeads([
        {
          portal: portal.trim(),
          listingRef: listingRef.trim(),
          title: title.trim(),
          ...(contactName.trim() === '' ? {} : { contactName: contactName.trim() }),
          ...(contactEmail.trim() === '' ? {} : { contactEmail: contactEmail.trim() }),
          ...(contactPhone.trim() === '' ? {} : { contactPhone: contactPhone.trim() }),
          ...(message.trim() === '' ? {} : { message: message.trim() }),
        },
      ]);
      props.onDone(result.created, result.merged);
    } catch (e) {
      props.onError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.importFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rh-demand-form" data-testid="demand-import-panel" aria-label={t('webAdmin.demands.importTitle')}>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.demands.importPortal')}
          <input value={portal} onChange={(e) => setPortal(e.target.value)} data-testid="import-portal" />
        </label>
        <label>
          {t('webAdmin.demands.importListingRef')}
          <input
            value={listingRef}
            onChange={(e) => setListingRef(e.target.value)}
            placeholder={t('webAdmin.demands.importListingRefPlaceholder')}
            data-testid="import-listing-ref"
          />
        </label>
        <label>
          {t('webAdmin.demands.importTitleField')}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('webAdmin.demands.importTitlePlaceholder')}
            data-testid="import-title"
          />
        </label>
      </div>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.demands.importName')}
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="import-name" />
        </label>
        <label>
          {t('webAdmin.demands.importEmail')}
          <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="import-email" />
        </label>
        <label>
          {t('webAdmin.demands.importPhone')}
          <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="import-phone" />
        </label>
      </div>
      <label>
        {t('webAdmin.demands.importMessage')}
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="import-message" />
      </label>
      <button
        type="button"
        className="rh-btn rh-btn--primary"
        disabled={busy || portal.trim() === '' || title.trim() === ''}
        data-testid="import-submit"
        onClick={() => void submit()}
      >
        {t('webAdmin.demands.importSubmit')}
      </button>
    </section>
  );
}

/** Krok UXP2 (P2-14): typ nemovitosti — stejná slova jako „Nová zakázka" (WIZARD_TEXTS.type*). */
const WANTED_KIND_OPTIONS = ['LAND', 'HOUSE', 'APARTMENT', 'COMMERCIAL', 'OTHER'] as const;

/** Krok UXP2 (P2-4): kam API posílá cestu pole (`issues[].path`) → testid vstupu. */
const FIELD_BY_ISSUE_PATH: readonly (readonly [string, string])[] = [
  ['wantedPriceFrom', 'price-from'],
  ['wantedPriceTo', 'price'],
  ['wantedAreaFrom', 'area-from'],
  ['wantedAreaTo', 'area-to'],
  ['wantedLocality', 'locality'],
  ['title', 'title'],
];

/** Ruční založení poptávky (telefon, schůzka, web). */
function DemandForm(props: {
  readonly onCreated: () => void;
  readonly onError: (message: string) => void;
}): React.JSX.Element {
  const t = useT();
  const [title, setTitle] = React.useState('');
  const [source, setSource] = React.useState<string>('phone');
  const [contactName, setContactName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [wantedKind, setWantedKind] = React.useState('');
  const [wantedLocality, setWantedLocality] = React.useState('');
  const [wantedPriceFrom, setWantedPriceFrom] = React.useState('');
  const [wantedPriceTo, setWantedPriceTo] = React.useState('');
  const [wantedDisposition, setWantedDisposition] = React.useState('');
  // Krok CRM4: výměra pro párování s nabídkami.
  const [wantedAreaFrom, setWantedAreaFrom] = React.useState('');
  const [wantedAreaTo, setWantedAreaTo] = React.useState('');
  const [nextStep, setNextStep] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Krok UXP2 (P2-4): chyba u KONKRÉTNÍHO pole, ne jen obecný toast nahoře.
  const [fieldError, setFieldError] = React.useState<{ readonly field: string; readonly message: string } | null>(
    null,
  );

  /**
   * Krok UXP2 (P2-14, nález „Výměra od 80, do 50 projde bez varování"):
   * obrácený rozsah se neposílá na server, hlásí se hned u pole od, a
   * fokus se na něj přesune (stejný vzor jako u chyby z API níže).
   */
  function rangeError(fromField: string, fromRaw: string, toRaw: string, message: string): boolean {
    const from = Number(normalizeNumberInput(fromRaw));
    const to = Number(normalizeNumberInput(toRaw));
    if (fromRaw.trim() === '' || toRaw.trim() === '' || !Number.isFinite(from) || !Number.isFinite(to)) return false;
    if (from <= to) return false;
    setFieldError({ field: fromField, message });
    focusField(`demand-form-${fromField}`);
    return true;
  }

  async function submit(): Promise<void> {
    setFieldError(null);
    if (rangeError('price-from', wantedPriceFrom, wantedPriceTo, t('webAdmin.demands.form.priceRangeInvalid'))) {
      return;
    }
    if (rangeError('area-from', wantedAreaFrom, wantedAreaTo, t('webAdmin.demands.form.areaRangeInvalid'))) {
      return;
    }
    setBusy(true);
    try {
      await createDemand({
        title: title.trim(),
        source: source as Demand['source'],
        ...(contactName.trim() === '' ? {} : { contactName: contactName.trim() }),
        ...(contactEmail.trim() === '' ? {} : { contactEmail: contactEmail.trim() }),
        ...(contactPhone.trim() === '' ? {} : { contactPhone: contactPhone.trim() }),
        ...(wantedKind.trim() === '' ? {} : { wantedKind: wantedKind.trim() }),
        ...(wantedLocality.trim() === '' ? {} : { wantedLocality: wantedLocality.trim() }),
        ...(wantedPriceFrom.trim() === '' ? {} : { wantedPriceFrom: normalizeNumberInput(wantedPriceFrom.trim()) }),
        ...(wantedPriceTo.trim() === '' ? {} : { wantedPriceTo: normalizeNumberInput(wantedPriceTo.trim()) }),
        ...(wantedDisposition.trim() === '' ? {} : { wantedDisposition: wantedDisposition.trim() }),
        ...(wantedAreaFrom.trim() === '' ? {} : { wantedAreaFrom: normalizeNumberInput(wantedAreaFrom.trim()) }),
        ...(wantedAreaTo.trim() === '' ? {} : { wantedAreaTo: normalizeNumberInput(wantedAreaTo.trim()) }),
        ...(nextStep.trim() === '' ? {} : { nextStep: nextStep.trim() }),
      });
      props.onCreated();
    } catch (e) {
      if (e instanceof DomainApiError && e.issues.length > 0) {
        for (const [field, testField] of FIELD_BY_ISSUE_PATH) {
          const issue = issueForField(e.issues, field);
          if (issue !== undefined) {
            setFieldError({ field: testField, message: e.message });
            focusField(`demand-form-${testField}`);
            break;
          }
        }
      }
      props.onError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.drawerSaveFailed'));
    } finally {
      setBusy(false);
    }
  }

  const disabledReason = title.trim() === '' ? t('webAdmin.demands.form.titleRequired') : null;

  function fieldErrorFor(field: string): React.JSX.Element | null {
    if (fieldError?.field !== field) return null;
    return (
      <p role="alert" className="rh-a2-error" data-testid={`demand-form-${field}-error`}>
        {fieldError.message}
      </p>
    );
  }

  return (
    <section className="rh-demand-form" data-testid="demand-form" aria-label={t('webAdmin.demands.newDemand')}>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.demands.form.titleLabel')}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('webAdmin.demands.form.titlePlaceholder')}
            aria-invalid={fieldError?.field === 'title' ? true : undefined}
            data-testid="demand-form-title"
          />
          {fieldErrorFor('title')}
        </label>
        <label>
          {t('webAdmin.demands.form.sourceLabel')}
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            data-testid="demand-form-source"
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`crm.source.${option}` as 'crm.source.portal')}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.demands.form.nameLabel')}
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="demand-form-name" />
        </label>
        <label>
          {t('webAdmin.demands.form.emailLabel')}
          <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} data-testid="demand-form-email" />
        </label>
        <label>
          {t('webAdmin.demands.form.phoneLabel')}
          <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="demand-form-phone" />
        </label>
      </div>
      <div className="rh-demand-form__row">
        <label>
          {t('webAdmin.demands.form.kindLabel')}
          <select value={wantedKind} onChange={(e) => setWantedKind(e.target.value)} data-testid="demand-form-kind">
            <option value="">{t('webAdmin.demands.form.kindAny')}</option>
            {WANTED_KIND_OPTIONS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`webAdmin.demands.form.kind${kind}` as 'webAdmin.demands.form.kindLAND')}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('webAdmin.demands.form.localityLabel')}
          <input
            value={wantedLocality}
            onChange={(e) => setWantedLocality(e.target.value)}
            aria-invalid={fieldError?.field === 'locality' ? true : undefined}
            data-testid="demand-form-locality"
          />
          {fieldErrorFor('locality')}
        </label>
        <label>
          {t('webAdmin.demands.form.priceFromLabel')}
          <input
            value={wantedPriceFrom}
            onChange={(e) => setWantedPriceFrom(e.target.value)}
            inputMode="numeric"
            aria-invalid={fieldError?.field === 'price-from' ? true : undefined}
            data-testid="demand-form-price-from"
          />
          {fieldErrorFor('price-from')}
        </label>
        <label>
          {t('webAdmin.demands.form.priceToLabel')}
          <input
            value={wantedPriceTo}
            onChange={(e) => setWantedPriceTo(e.target.value)}
            inputMode="numeric"
            aria-invalid={fieldError?.field === 'price' ? true : undefined}
            data-testid="demand-form-price"
          />
          {fieldErrorFor('price')}
        </label>
        <label>
          {t('webAdmin.demands.form.dispositionLabel')}
          <input
            value={wantedDisposition}
            onChange={(e) => setWantedDisposition(e.target.value)}
            data-testid="demand-form-disposition"
          />
        </label>
        <label>
          {t('webAdmin.demands.form.areaFromLabel')}
          <input
            value={wantedAreaFrom}
            onChange={(e) => setWantedAreaFrom(e.target.value)}
            inputMode="numeric"
            aria-invalid={fieldError?.field === 'area-from' ? true : undefined}
            data-testid="demand-form-area-from"
          />
          {fieldErrorFor('area-from')}
        </label>
        <label>
          {t('webAdmin.demands.form.areaToLabel')}
          <input
            value={wantedAreaTo}
            onChange={(e) => setWantedAreaTo(e.target.value)}
            inputMode="numeric"
            aria-invalid={fieldError?.field === 'area-to' ? true : undefined}
            data-testid="demand-form-area-to"
          />
          {fieldErrorFor('area-to')}
        </label>
        <label>
          {t('webAdmin.demands.form.nextStepLabel')}
          <input value={nextStep} onChange={(e) => setNextStep(e.target.value)} data-testid="demand-form-next-step" />
        </label>
      </div>
      <button
        type="button"
        className="rh-btn rh-btn--primary"
        disabled={busy || disabledReason !== null}
        data-testid="demand-form-submit"
        onClick={() => void submit()}
      >
        {t('webAdmin.demands.form.submit')}
      </button>
      {disabledReason !== null ? (
        <p className="text-xs m-0" style={{ color: 'var(--rh-text-muted)' }} data-testid="demand-form-submit-reason">
          {disabledReason}
        </p>
      ) : null}
    </section>
  );
}
